import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/status";
import { roleLabel } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Power, PowerOff, Shield, Trash2, MailPlus } from "lucide-react";
import TeamsSettings from "@/components/TeamsSettings";
import ReasonSettings from "@/components/ReasonSettings";
import PartNameSettings from "@/components/PartNameSettings";
import AddUserForm from "@/components/AddUserForm";
import DangerZone from "@/components/DangerZone";

const ROLES = ["admin", "lead", "staff", "management", "kiosk", "maintenance"];
const fmtLogin = (v) => v ? fmtDateTime(v) : "Never";
const ROLE_COLOR = {
  admin: "bg-[#424e43]/12 text-[#424e43] border-[#424e43]/45",
  lead: "bg-[#647265]/15 text-[#647265] border-[#647265]/50",
  staff: "bg-[#b1b59c]/25 text-[#4d4c4b] border-[#b1b59c]",
  management: "bg-[#a39f98]/25 text-[#4d4c4b] border-[#a39f98]",
  kiosk: "bg-indigo-100 text-indigo-800 border-indigo-300",
  maintenance: "bg-amber-100 text-amber-800 border-amber-300",
};

export default function Settings() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [activityTarget, setActivityTarget] = useState(null);
  const [updatingActivity, setUpdatingActivity] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setRole = async (uid, role) => {
    try {
      const { data } = await api.patch(`/users/${uid}/role`, { role });
      setUsers((us) => us.map((u) => u.id === uid ? data : u));
      toast.success(`Role updated → ${role}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const removeUser = async () => {
    if (!removeTarget) return;

    setRemoving(true);
    try {
      await api.delete(`/users/${removeTarget.id}`);
      if (removeTarget.id === user.id) {
        toast.success("Your account was removed. Signing out…");
        setRemoveTarget(null);
        await logout();
        return;
      }
      setUsers((currentUsers) => currentUsers.filter((member) => member.id !== removeTarget.id));
      toast.success(`${removeTarget.name || removeTarget.email} removed`);
      setRemoveTarget(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Could not remove user");
    } finally {
      setRemoving(false);
    }
  };

  const [inviting, setInviting] = useState(null);
  const resendInvite = async (member) => {
    setInviting(member.id);
    try {
      const { data } = await api.post(`/users/${member.id}/invite`);
      setUsers((us) => us.map((u) => (u.id === data.id ? data : u)));
      toast.success(`Invite emailed to ${data.email}`);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Could not send invite");
    } finally {
      setInviting(null);
    }
  };

  const updateActivity = async () => {
    if (!activityTarget) return;

    const nextIsActive = activityTarget.is_active === false;
    setUpdatingActivity(true);
    try {
      const { data } = await api.patch(`/users/${activityTarget.id}/activity`, {
        is_active: nextIsActive,
      });
      setUsers((currentUsers) =>
        currentUsers.map((member) => (member.id === data.id ? data : member)),
      );
      toast.success(`${data.name || data.email} ${nextIsActive ? "enabled" : "disabled"}`);
      setActivityTarget(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Could not update access");
    } finally {
      setUpdatingActivity(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Manage team members and their access to the Rework Hub."
        testid="settings-header"
      />

      <div className="p-6 md:p-10 space-y-8">
        <TeamsSettings />
        <ReasonSettings />
        <PartNameSettings />
        <AddUserForm onAdded={(u) => setUsers((us) => [u, ...us])} />
        {user?.email === "online@misco.kiwi" && <DangerZone />}
        <div className="border border-[#e6e5df] bg-white rounded-sm">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <Shield size={16} className="text-[#647265]" />
            <div className="font-display font-bold text-[#232826]">User Roles</div>
            <div className="ml-auto text-xs font-mono text-[#6b6a68]">{users.length} member{users.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="hidden w-full max-w-full overflow-hidden md:block">
            <div className="w-full max-w-full overflow-x-auto" data-testid="users-table-scroll">
              <table className="w-full min-w-[760px] text-sm" data-testid="users-table">
              <thead className="bg-[#efece5]">
                <tr className="border-b border-[#e6e5df]">
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-widest text-[#6b6a68] font-medium">Name</th>
                  <th className="text-left py-3 text-[10px] uppercase tracking-widest text-[#6b6a68] font-medium">Email</th>
                  <th className="text-left py-3 text-[10px] uppercase tracking-widest text-[#6b6a68] font-medium">Current Role</th>
                  <th className="text-left py-3 text-[10px] uppercase tracking-widest text-[#6b6a68] font-medium">Last Sign-in</th>
                  <th className="px-5 py-3 text-left text-[10px] font-medium uppercase tracking-widest text-[#6b6a68]">
                    Access
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center font-mono text-xs text-[#6b6a68]">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && users.map((u) => (
                  <tr key={u.id} className="border-b border-[#e6e5df] hover:bg-[#efece5]/60" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 text-[#232826]">{u.name}</td>
                    <td className="py-3 text-[#6b6a68] mono text-xs">{u.email}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm border text-[11px] font-medium uppercase tracking-wider ${ROLE_COLOR[u.role]}`}>
                          {roleLabel(u.role)}
                        </span>
                        <span
                          data-testid={`user-state-${u.id}`}
                          className={`text-[10px] font-bold uppercase tracking-widest ${
                            u.is_active === false ? "text-[#8a3a2a]" : "text-[#647265]"
                          }`}
                        >
                          {u.is_active === false ? "Inactive" : "Active"}
                        </span>
                      </div>
                    </td>
                    <td className={`py-3 mono text-xs ${u.last_login ? "text-[#6b6a68]" : "text-[#a39f98] italic"}`} data-testid={`user-last-login-${u.id}`}>
                      {fmtLogin(u.last_login)}
                      {!u.last_login && u.auth_provider === "entra" && (
                        <div className={`mt-1 text-[10px] not-italic ${u.invite_error ? "text-[#8a3a2a]" : "text-[#a39f98]"}`} data-testid={`user-invite-status-${u.id}`}>
                          {u.invite_error ? "Invite failed" : u.invite_sent_at ? `Invited ${fmtLogin(u.invite_sent_at)}` : "Invite not sent"}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.id === user.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#a39f98]">You</span>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRemoveTarget(u)}
                            data-testid={`remove-user-${u.id}`}
                            className="h-8 border-[#8a3a2a]/40 bg-white px-2 text-[#8a3a2a] hover:bg-[#8a3a2a]/10 hover:text-[#6a2a1e]"
                          >
                            <Trash2 size={13} aria-hidden="true" />
                            <span className="sr-only">Remove my account</span>
                          </Button>
                        </div>
                      ) : (
                        <div className="flex min-w-0 items-center gap-2">
                          <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                            <SelectTrigger
                              className="h-8 w-[150px] rounded-sm border-[#cfcfc9] bg-white text-xs text-[#232826]"
                              data-testid={`role-select-${u.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-[#e6e5df] bg-white">
                              {ROLES.map((r) => (
                                <SelectItem key={r} value={r}>
                                  {roleLabel(r)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {u.auth_provider === "entra" && u.is_active !== false && (
                            <Button
                              type="button"
                              variant="outline"
                              title="Send / resend invite email"
                              disabled={inviting === u.id}
                              onClick={() => resendInvite(u)}
                              data-testid={`invite-user-${u.id}`}
                              className="h-8 border-[#647265]/40 bg-white px-2 text-[#424e43] hover:bg-[#647265]/10"
                            >
                              <MailPlus size={13} aria-hidden="true" />
                              <span className="sr-only">Resend invite to {u.name || u.email}</span>
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            title={u.is_active === false ? "Enable user" : "Disable user"}
                            onClick={() => setActivityTarget(u)}
                            data-testid={`${u.is_active === false ? "enable" : "disable"}-user-${u.id}`}
                            className="h-8 border-[#647265]/40 bg-white px-2 text-[#424e43] hover:bg-[#647265]/10"
                          >
                            {u.is_active === false ? (
                              <Power size={13} aria-hidden="true" />
                            ) : (
                              <PowerOff size={13} aria-hidden="true" />
                            )}
                            <span className="sr-only">
                              {u.is_active === false ? "Enable" : "Disable"} {u.name || u.email}
                            </span>
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setRemoveTarget(u)}
                            data-testid={`remove-user-${u.id}`}
                            className="h-8 border-[#8a3a2a]/40 bg-white px-2 text-[#8a3a2a] hover:bg-[#8a3a2a]/10 hover:text-[#6a2a1e]"
                          >
                            <Trash2 size={13} aria-hidden="true" />
                            <span className="sr-only">Remove {u.name || u.email}</span>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
          <div className="divide-y divide-[#e6e5df] md:hidden" data-testid="mobile-users-list">
            {loading && (
              <div className="py-12 text-center font-mono text-xs text-[#6b6a68]">Loading…</div>
            )}
            {!loading && users.map((member) => (
              <div
                key={member.id}
                data-testid={`mobile-user-row-${member.id}`}
                className="space-y-3 p-4"
              >
                <div>
                  <div className="font-medium text-[#232826]">{member.name}</div>
                  <div className="mono mt-1 break-all text-xs text-[#6b6a68]">{member.email}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#a39f98]" data-testid={`mobile-user-last-login-${member.id}`}>
                    Last sign-in: {fmtLogin(member.last_login)}
                  </div>
                </div>
                {member.id === user.id ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge className={`${ROLE_COLOR[member.role]} rounded-sm uppercase`}>
                        {roleLabel(member.role)}
                      </Badge>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#647265]">
                        Active
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#a39f98]">You</span>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRemoveTarget(member)}
                        data-testid={`mobile-remove-user-${member.id}`}
                        className="h-10 w-10 shrink-0 border-[#8a3a2a]/40 bg-white p-0 text-[#8a3a2a]"
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        <span className="sr-only">Remove my account</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Select value={member.role} onValueChange={(role) => setRole(member.id, role)}>
                      <SelectTrigger
                        className="h-10 min-w-0 flex-1 rounded-sm border-[#cfcfc9] bg-white text-xs"
                        data-testid={`mobile-role-select-${member.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-[#e6e5df] bg-white">
                        {ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {roleLabel(role)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {member.auth_provider === "entra" && member.is_active !== false && (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={inviting === member.id}
                        onClick={() => resendInvite(member)}
                        data-testid={`mobile-invite-user-${member.id}`}
                        className="h-10 w-10 shrink-0 border-[#647265]/40 bg-white p-0 text-[#424e43]"
                      >
                        <MailPlus size={16} aria-hidden="true" />
                        <span className="sr-only">Resend invite to {member.name || member.email}</span>
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      title={member.is_active === false ? "Enable user" : "Disable user"}
                      onClick={() => setActivityTarget(member)}
                      data-testid={`${member.is_active === false ? "mobile-enable" : "mobile-disable"}-user-${member.id}`}
                      className="h-10 w-10 shrink-0 border-[#647265]/40 bg-white p-0 text-[#424e43]"
                    >
                      {member.is_active === false ? (
                        <Power size={16} aria-hidden="true" />
                      ) : (
                        <PowerOff size={16} aria-hidden="true" />
                      )}
                      <span className="sr-only">
                        {member.is_active === false ? "Enable" : "Disable"} {member.name || member.email}
                      </span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setRemoveTarget(member)}
                      data-testid={`mobile-remove-user-${member.id}`}
                      className="h-10 w-10 shrink-0 border-[#8a3a2a]/40 bg-white p-0 text-[#8a3a2a]"
                    >
                      <Trash2 size={16} aria-hidden="true" />
                      <span className="sr-only">Remove {member.name || member.email}</span>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#e6e5df] bg-white rounded-sm p-6">
          <div className="font-display font-bold mb-4 text-[#232826]">Role Permissions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="border border-[#e6e5df] p-4 rounded-sm">
              <Badge className={`${ROLE_COLOR.kiosk} rounded-sm mb-2 uppercase`}>Kiosk</Badge>
              <p className="text-[#6b6a68]">Factory touchscreen / tablet. Sees the Board, Rack, Maintenance and Scan pages; can advance stages. 30-day sign-in.</p>
            </div>
            <div className="border border-[#e6e5df] p-4 rounded-sm">
              <Badge className={`${ROLE_COLOR.admin} rounded-sm mb-2 uppercase`}>Admin</Badge>
              <p className="text-[#6b6a68]">Full access. Update statuses, assign, comment, delete records, manage user roles.</p>
            </div>
            <div className="border border-[#e6e5df] p-4 rounded-sm">
              <Badge className={`${ROLE_COLOR.lead} rounded-sm mb-2 uppercase`}>Lead</Badge>
              <p className="text-[#6b6a68]">Update statuses, assign owners, add comments on any rework request.</p>
            </div>
            <div className="border border-[#e6e5df] p-4 rounded-sm">
              <Badge className={`${ROLE_COLOR.staff} rounded-sm mb-2 uppercase`}>Staff</Badge>
              <p className="text-[#6b6a68]">Submit new requests, view register, add comments.</p>
            </div>
            <div className="border border-[#e6e5df] p-4 rounded-sm">
              <Badge className={`${ROLE_COLOR.management} rounded-sm mb-2 uppercase`}>Read Only</Badge>
              <p className="text-[#6b6a68]">View-only. Sees the dashboard, register, boards and details. No submitting, editing, advancing, releasing, scanning or settings.</p>
            </div>
          </div>
        </div>
      </div>
      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => {
          if (!open && !removing) setRemoveTarget(null);
        }}
      >
        <AlertDialogContent className="rounded-sm border-[#e6e5df] bg-white" data-testid="remove-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#232826]">
              Remove user?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b6a68]">
              {removeTarget?.id === user.id
                ? "This is your own account — you'll be signed out immediately and will need another admin to add you back."
                : `${removeTarget?.name || removeTarget?.email} will lose access to the Rework Hub.`}{" "}
              Existing rework records and comments will be retained.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-user-cancel" disabled={removing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={removeUser}
              disabled={removing}
              data-testid="remove-user-confirm"
              className="rounded-sm bg-[#8a3a2a] text-white hover:bg-[#6a2a1e]"
            >
              {removing ? "Removing…" : "Remove user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!activityTarget}
        onOpenChange={(open) => {
          if (!open && !updatingActivity) setActivityTarget(null);
        }}
      >
        <AlertDialogContent
          className="rounded-sm border-[#e6e5df] bg-white"
          data-testid="user-activity-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#232826]">
              {activityTarget?.is_active === false ? "Enable user?" : "Disable user?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b6a68]">
              {activityTarget?.name || activityTarget?.email} will {
                activityTarget?.is_active === false ? "regain" : "lose"
              } sign-in access. Their rework records and comments will remain available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-activity-cancel" disabled={updatingActivity}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={updateActivity}
              disabled={updatingActivity}
              data-testid="user-activity-confirm"
              className="rounded-sm bg-[#424e43] text-white hover:bg-[#232826]"
            >
              {updatingActivity
                ? "Updating…"
                : activityTarget?.is_active === false
                  ? "Enable user"
                  : "Disable user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
